import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Factory,
  ClipboardList,
  MapPin,
  Package,
  Plus,
  RefreshCcw,
  Search,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { getLocalYMD } from '../utils/storeLogistics';
import { fetchOperationalProductions } from '../utils/logisticsPlanning';
import {
  PRODUCTION_REQUEST_SOURCE_TYPES,
  buildProductionRequestRows,
  filterOperationalProductionRequestLines,
  reconcileProductionRequestLinesWithOrders,
} from '../utils/productionOrders';
import { applyActiveProductsFilter } from '../utils/productStatus';

const fmt = (value) =>
  Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const EMPTY_MANUAL_FORM = {
  productId: '',
  quantity: '',
  notes: '',
};

const formatLongDate = (value) => {
  if (!value) return 'Sin fecha';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
};

const formatShortDate = (value) => {
  if (!value) return 'Sin fecha';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'short',
  });
};

const buildManualSourceKey = (productId) =>
  `manual:${productId || 'sin-producto'}:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`;

const buildProductionSpanRange = (startDate, endDate) => {
  const [startYear, startMonth, startDay] = String(startDate || getLocalYMD(new Date()))
    .split('-')
    .map(Number);
  const [endYear, endMonth, endDay] = String(endDate || startDate || getLocalYMD(new Date()))
    .split('-')
    .map(Number);

  return {
    startISO: new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0).toISOString(),
    endISO: new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999).toISOString(),
  };
};

function StepTimeline({ steps }) {
  if (!steps.length) {
    return <div className="text-[10px] font-bold text-amber-700">Entra para definir el flujo</div>;
  }

  return (
    <div className="min-w-[220px]">
      <div className="flex items-center gap-1.5">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            <div
              title={`${step.name}: ${fmt(step.completed)} / ${fmt(step.target)}`}
              className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-[10px] font-black ${
                step.isComplete
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-slate-200 bg-white text-slate-500'
              }`}
            >
              {step.position}
            </div>
            {index < steps.length - 1 ? (
              <div className={`h-[2px] w-5 rounded-full ${step.isComplete ? 'bg-emerald-400' : 'bg-slate-200'}`} />
            ) : null}
          </React.Fragment>
        ))}
      </div>
      <p className="mt-2 text-[9px] font-bold uppercase tracking-widest text-slate-400">
        {steps.filter((step) => step.isComplete).length}/{steps.length} pasos completos
      </p>
    </div>
  );
}

export default function PedidosProduccion() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialDate = searchParams.get('date') || getLocalYMD(new Date());

  // ✅ FIX bug #2 multi-sucursal: el sitio activo viene del SiteContext. Si vino por
  // querystring (?siteId=...) lo respetamos para deep-links, pero el cambio del Topbar
  // se sincroniza vía useEffect.
  const { selectedSiteId: contextSiteId, setSelectedSiteId: setContextSiteId } = useSite();
  const initialSiteId = searchParams.get('siteId') || contextSiteId || '';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedSiteId, setSelectedSiteIdLocal] = useState(initialSiteId);

  const setSelectedSiteId = (next) => {
    const value = next || '';
    setSelectedSiteIdLocal(value);
    // Sincronizamos hacia el contexto para que toda la app vea el mismo valor.
    setContextSiteId(value);
  };

  // Si cambia desde el Topbar mientras estamos en esta página, refrescamos.
  useEffect(() => {
    setSelectedSiteIdLocal(contextSiteId || '');
  }, [contextSiteId]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sitios, setSitios] = useState([]);
  const [products, setProducts] = useState([]);
  const [stockLevels, setStockLevels] = useState([]);
  const [requestLines, setRequestLines] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [stepEntries, setStepEntries] = useState([]);
  const [productions, setProductions] = useState([]);
  const [manualFormOpen, setManualFormOpen] = useState(false);
  const [manualForm, setManualForm] = useState(EMPTY_MANUAL_FORM);
  const [savingManual, setSavingManual] = useState(false);
  const [rowDateSelections, setRowDateSelections] = useState({});

  // ✅ FIX bug #2: persistencia centralizada en SiteContext.

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadBaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    fetchDailyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, selectedDate, selectedSiteId]);

  const loadBaseData = async () => {
    try {
      const orgId = profile.organization_id;
      const [sitesRes, productsRes, stockLevelsRes] = await Promise.all([
        supabase
          .from('sitios')
          .select('id, nombre')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .order('nombre'),
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select('id, name, is_active, brand')
          .eq('organization_id', orgId)
        )
          .order('name'),
        supabase
          .from('stock_levels')
          .select('product_id, site_id, quantity')
          .eq('organization_id', orgId),
      ]);

      if (sitesRes.error) throw sitesRes.error;
      if (productsRes.error) throw productsRes.error;
      if (stockLevelsRes.error) throw stockLevelsRes.error;

      setSitios(sitesRes.data || []);
      setProducts(productsRes.data || []);
      setStockLevels(stockLevelsRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error('No se pudo cargar la base del modulo.');
    }
  };

  const fetchDailyData = async () => {
    const firstLoad = loading;
    if (!firstLoad) setRefreshing(true);

    try {
      const orgId = profile.organization_id;

      let requestQuery = supabase
        .from('production_request_lines')
        .select('*')
        .eq('organization_id', orgId)
        .gte('requested_date', selectedDate)
        .eq('is_active', true)
        .order('requested_date', { ascending: true })
        .order('created_at', { ascending: true });

      if (selectedSiteId) {
        requestQuery = requestQuery.eq('site_id', selectedSiteId);
      }

      const [requestRes, workflowRes, productsRes, stockLevelsRes] = await Promise.all([
        requestQuery,
        supabase
          .from('product_production_workflows')
          .select('*')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .order('updated_at', { ascending: false }),
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select('id, name, is_active, brand')
          .eq('organization_id', orgId)
        )
          .order('name'),
        supabase
          .from('stock_levels')
          .select('product_id, site_id, quantity')
          .eq('organization_id', orgId),
      ]);

      if (requestRes.error) throw requestRes.error;
      if (workflowRes.error) throw workflowRes.error;
      if (productsRes.error) throw productsRes.error;
      if (stockLevelsRes.error) throw stockLevelsRes.error;

      const reconciledRequestLines = await reconcileProductionRequestLinesWithOrders({
        supabase,
        organizationId: orgId,
        lines: filterOperationalProductionRequestLines(requestRes.data || []),
        deactivateClosedLogistics: true,
      });

      if (reconciledRequestLines.error) {
        console.error(reconciledRequestLines.error);
      }

      // FIX CRÍTICO #7: avisar al operador si hubo líneas con pedido cerrado pero
      // producción ya en marcha — esas líneas NO se desactivaron para preservar trazabilidad.
      const skippedCount = reconciledRequestLines.skippedLineIds?.length || 0;
      if (skippedCount > 0) {
        toast(
          `${skippedCount} línea(s) de pedido cerrado se conservaron porque tienen producción asociada. ` +
          `Revisá si corresponde anular o reasignar manualmente.`,
          { icon: '⚠️', duration: 8000 },
        );
      }

      const activeRequestLines = filterOperationalProductionRequestLines(reconciledRequestLines.data || []);
      const maxRequestedDate = activeRequestLines.reduce(
        (acc, line) => (String(line.requested_date || '') > acc ? String(line.requested_date || '') : acc),
        selectedDate,
      );
      const { startISO, endISO } = buildProductionSpanRange(selectedDate, maxRequestedDate);

      const stepEntriesQuery = supabase
        .from('production_step_entries')
        .select('*')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .gte('operational_date', selectedDate)
        .lte('operational_date', maxRequestedDate)
        .order('operational_date', { ascending: true })
        .order('created_at', { ascending: true });

      const [stepRes, productionRes] = await Promise.all([
        stepEntriesQuery,
        fetchOperationalProductions({
          supabase,
          organizationId: orgId,
          selectedSiteId: '',
          range: {
            startYMD: selectedDate,
            endYMD: maxRequestedDate,
            startISO,
            endISO,
          },
        }),
      ]);

      if (stepRes.error) throw stepRes.error;
      if (productionRes.error) throw productionRes.error;

      setRequestLines(activeRequestLines);
      setWorkflows(workflowRes.data || []);
      setStepEntries(stepRes.data || []);
      setProductions(productionRes.data || []);
      setProducts(productsRes.data || []);
      setStockLevels(stockLevelsRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error('No se pudo sincronizar pedidos de produccion.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const stockByProductId = useMemo(() => {
    const map = new Map();

    (stockLevels || []).forEach((level) => {
      const productId = level?.product_id;
      if (!productId) return;
      if (selectedSiteId && level.site_id !== selectedSiteId) return;

      map.set(productId, Number(map.get(productId) || 0) + Number(level.quantity || 0));
    });

    return map;
  }, [selectedSiteId, stockLevels]);

  const rows = useMemo(
    () =>
      buildProductionRequestRows({
        requestLines,
        workflows,
        stepEntries,
        productions,
        products,
        selectedSiteId,
        stockByProductId,
      }),
    [productions, products, requestLines, selectedSiteId, stepEntries, stockByProductId, workflows],
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = String(searchTerm || '').trim().toLowerCase();
    if (!normalizedSearch) return rows;
    return rows.filter((row) => row.name.toLowerCase().includes(normalizedSearch));
  }, [rows, searchTerm]);

  useEffect(() => {
    setRowDateSelections((current) => {
      const next = { ...current };
      let changed = false;
      const validKeys = new Set(rows.map((row) => row.key));

      Object.keys(next).forEach((rowKey) => {
        if (!validKeys.has(rowKey)) {
          delete next[rowKey];
          changed = true;
        }
      });

      rows.forEach((row) => {
        const preferredDate =
          row.requestedDates.find((date) => Number(row.getMetricsForDate(date).pendingProduction || 0) > 0) ||
          row.requestedDates[0] ||
          selectedDate;

        if (!next[row.key] || !row.requestedDates.includes(next[row.key])) {
          next[row.key] = preferredDate;
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [rows, selectedDate]);

  const rowsWithMetrics = useMemo(
    () =>
      filteredRows.map((row) => {
        const requestedDate =
          rowDateSelections[row.key] && row.requestedDates.includes(rowDateSelections[row.key])
            ? rowDateSelections[row.key]
            : row.requestedDates[0] || selectedDate;

        return {
          row,
          requestedDate,
          metrics: row.getMetricsForDate(requestedDate),
        };
      }),
    [filteredRows, rowDateSelections, selectedDate],
  );

  const selectedSiteName = selectedSiteId
    ? sitios.find((site) => site.id === selectedSiteId)?.nombre || 'Sitio'
    : 'Consolidado';

  const productOptions = useMemo(
    () => (products || []).map((product) => ({ value: product.id, label: product.name })),
    [products],
  );

  const totals = useMemo(
    () =>
      rowsWithMetrics.reduce(
        (acc, item) => ({
          pedido: acc.pedido + Number(item.metrics.pedido || 0),
          pendingProduction: acc.pendingProduction + Number(item.metrics.pendingProduction || 0),
          finalCompleted: acc.finalCompleted + Number(item.metrics.finalCompleted || 0),
        }),
        { pedido: 0, pendingProduction: 0, finalCompleted: 0 },
      ),
    [rowsWithMetrics],
  );

  const rowsWithoutWorkflow = filteredRows.filter((row) => row.productId && row.steps.length === 0).length;
  const pendingDatesCount = useMemo(
    () =>
      rows.reduce((acc, row) => {
        const pendingDates = row.requestedDates.filter(
          (date) => Number(row.getMetricsForDate(date).pendingProduction || 0) > 0,
        );
        return acc + pendingDates.length;
      }, 0),
    [rows],
  );

  const handleManualFormChange = (field, value) => {
    setManualForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleCreateManualLine = async (event) => {
    event.preventDefault();
    if (!profile?.organization_id) return;
    if (!manualForm.productId) return toast.error('Selecciona un producto.');

    const quantity = Number(manualForm.quantity || 0);
    if (!Number.isFinite(quantity) || quantity <= 0) return toast.error('Ingresa una cantidad valida.');

    const product = products.find((item) => item.id === manualForm.productId);
    if (!product) return toast.error('No encontramos el producto seleccionado.');

    setSavingManual(true);

    try {
      const { error } = await supabase.from('production_request_lines').insert({
        organization_id: profile.organization_id,
        source_key: buildManualSourceKey(product.id),
        source_type: PRODUCTION_REQUEST_SOURCE_TYPES.MANUAL,
        site_id: selectedSiteId || null,
        product_id: product.id,
        product_name: product.name,
        quantity,
        requested_date: selectedDate,
        notes: manualForm.notes || '',
        metadata: {
          created_manually: true,
          product_name: product.name,
        },
        is_active: true,
      });

      if (error) throw error;

      toast.success('Pedido manual agregado.');
      setManualForm(EMPTY_MANUAL_FORM);
      setManualFormOpen(false);
      fetchDailyData();
    } catch (error) {
      console.error(error);
      toast.error('No se pudo guardar la carga manual.');
    } finally {
      setSavingManual(false);
    }
  };

  const handleGoToProduction = (row, requestedDate, metrics) => {
    if (!row.productId) {
      toast.error('El pedido necesita un producto vinculado para registrar produccion.');
      return;
    }

    if (!row.steps.length) {
      navigate(`/sistema/produccion-flujos?productId=${row.productId}`);
      return;
    }

    const params = new URLSearchParams();
    params.set('productId', row.productId);
    params.set('requestedDate', requestedDate || selectedDate);
    if (metrics?.lines?.length > 0) params.set('requestLineIds', metrics.lines.map((line) => line.id).join(','));
    if (selectedSiteId || row.primarySiteId) params.set('siteId', selectedSiteId || row.primarySiteId);

    navigate(`/sistema/produccion-detalle?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={36} />
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">
          Cargando pedidos de produccion...
        </p>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in flex min-h-[calc(100vh-24px)] flex-col">
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/sistema/logistica')}
            className="w-10 h-10 rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#51A7FD] transition-all flex items-center justify-center shrink-0"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <ClipboardList size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              Pedidos de produccion
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Vista diaria con pendientes por fecha y acceso directo al detalle.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <CalendarDays size={14} className="text-slate-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 outline-none"
            />
          </div>

          <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <MapPin size={14} className="text-slate-400" />
            <select
              value={selectedSiteId}
              onChange={(event) => setSelectedSiteId(event.target.value)}
              className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 outline-none cursor-pointer"
            >
              <option value="">Consolidado</option>
              {sitios.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.nombre}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={fetchDailyData}
            disabled={refreshing}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCcw size={14} className={refreshing ? 'animate-spin' : ''} /> Actualizar
          </button>

          <button
            type="button"
            onClick={() => setManualFormOpen((current) => !current)}
            className="px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:brightness-95 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Plus size={14} /> Carga manual
          </button>
        </div>
      </div>

      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pedido visible</p>
            <ClipboardList size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmt(totals.pedido)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {rowsWithMetrics.length} producto(s) desde {formatShortDate(selectedDate)}
          </p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pendiente de producir</p>
            <Factory size={16} className={totals.pendingProduction > 0 ? 'text-amber-500' : 'text-emerald-500'} />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${totals.pendingProduction > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
            {fmt(totals.pendingProduction)}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Falta completar el flujo pedido
          </p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Produccion finalizada</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-emerald-600 tracking-tight">{fmt(totals.finalCompleted)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Ya ingreso a stock final
          </p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Fechas pendientes</p>
            <CalendarDays size={16} className={pendingDatesCount > 0 ? 'text-slate-500' : 'text-emerald-500'} />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${pendingDatesCount > 0 ? 'text-slate-800' : 'text-emerald-600'}`}>
            {pendingDatesCount}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Fechas aun abiertas desde esta vista
          </p>
        </div>
      </div>

      {manualFormOpen ? (
        <form
          onSubmit={handleCreateManualLine}
          className="mb-3 rounded-[26px] border border-slate-200 bg-[linear-gradient(135deg,_#ffffff_0%,_#f8fbff_100%)] p-4 shadow-sm"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Carga manual</p>
              <h3 className="mt-1 text-sm font-black uppercase tracking-tight text-slate-800">
                Sumar pedido sin venir de logistica
              </h3>
              <p className="mt-1 text-[10px] font-bold text-slate-500">
                Fecha: {formatLongDate(selectedDate)} - Sitio: {selectedSiteName}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setManualFormOpen(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 transition-all hover:border-[#51A7FD] hover:text-[#0B2A36]"
            >
              <X size={16} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,_2fr)_160px_minmax(0,_1.4fr)_auto]">
            <select
              value={manualForm.productId}
              onChange={(event) => handleManualFormChange('productId', event.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
            >
              <option value="">Seleccionar producto...</option>
              {productOptions.map((product) => (
                <option key={product.value} value={product.value}>
                  {product.label}
                </option>
              ))}
            </select>

            <input
              type="number"
              min="0"
              step="any"
              value={manualForm.quantity}
              onChange={(event) => handleManualFormChange('quantity', event.target.value)}
              placeholder="Cantidad"
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
            />

            <input
              type="text"
              value={manualForm.notes}
              onChange={(event) => handleManualFormChange('notes', event.target.value)}
              placeholder="Detalle u origen manual"
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD]"
            />

            <button
              type="submit"
              disabled={savingManual}
              className="rounded-2xl bg-[#3FF2AA] px-5 py-3 text-[10px] font-black uppercase tracking-widest text-[#0B2A36] shadow-lg transition-all hover:scale-[1.01] disabled:opacity-60"
            >
              {savingManual ? 'Guardando...' : 'Agregar'}
            </button>
          </div>
        </form>
      ) : null}

      <div className="flex min-h-[420px] flex-1 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Vista diaria</p>
            <h3 className="mt-1 text-sm font-black uppercase tracking-tight text-slate-800">
              {formatLongDate(selectedDate)}
            </h3>
          </div>

          <div className="relative w-full md:max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar producto..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-9 pr-4 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD]"
            />
          </div>
        </div>

        <div className="custom-scrollbar flex-1 overflow-x-auto overflow-y-auto bg-white">
          <table className="min-w-full text-left border-collapse">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-400">
                <th className="px-4 py-3">Producto</th>
                <th className="px-4 py-3 text-right">Pedido</th>
                <th className="px-4 py-3 text-right">Pendiente de producir</th>
                <th className="px-4 py-3">Proceso</th>
                <th className="px-4 py-3 text-right">Produccion finalizada</th>
                <th className="px-4 py-3 text-right">Stock</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-50">
              {rowsWithMetrics.length > 0 ? (
                rowsWithMetrics.map(({ row, requestedDate, metrics }) => (
                  <tr
                    key={`${row.key}-${requestedDate}`}
                    onClick={() => handleGoToProduction(row, requestedDate, metrics)}
                    className={`transition-colors ${row.productId ? 'cursor-pointer hover:bg-slate-50/70' : 'cursor-default bg-slate-50/30'}`}
                  >
                    <td className="px-4 py-3">
                      <div className="min-w-[220px]">
                        <p className="text-[11px] font-black uppercase tracking-tight text-slate-800 flex items-center gap-1.5 flex-wrap">
                          <span>{row.name}</span>
                          {row.brand && (
                            <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                              {row.brand}
                            </span>
                          )}
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          {row.requestedDates.length > 1 ? (
                            <select
                              value={requestedDate}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) =>
                                setRowDateSelections((current) => ({
                                  ...current,
                                  [row.key]: event.target.value,
                                }))
                              }
                              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-600 outline-none"
                            >
                              {row.requestedDates.map((date) => {
                                const dateMetrics = row.getMetricsForDate(date);
                                return (
                                  <option key={date} value={date}>
                                    {`${formatShortDate(date)} · pend ${fmt(dateMetrics.pendingProduction)}`}
                                  </option>
                                );
                              })}
                            </select>
                          ) : (
                            <span className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-500">
                              Fecha {formatShortDate(requestedDate)}
                            </span>
                          )}

                          <span
                            className={`inline-flex items-center rounded-xl border px-3 py-2 text-[9px] font-black uppercase tracking-widest ${
                              row.steps.length > 0
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : 'border-amber-200 bg-amber-50 text-amber-700'
                            }`}
                          >
                            {row.steps.length > 0 ? 'Flujo configurado' : 'Sin flujo'}
                          </span>
                        </div>
                        {metrics.pendingProduction <= 0 && metrics.finalCompleted > 0 ? (
                          <p className="mt-2 text-[9px] font-black uppercase tracking-widest text-emerald-600">
                            Produccion completada
                          </p>
                        ) : null}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex min-w-[88px] items-center justify-end rounded-xl bg-slate-50 px-3 py-2 text-[11px] font-black text-slate-700">
                        {fmt(metrics.pedido)}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span
                        className={`inline-flex min-w-[88px] items-center justify-end rounded-xl px-3 py-2 text-[11px] font-black ${
                          metrics.pendingProduction > 0
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {fmt(metrics.pendingProduction)}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <StepTimeline steps={metrics.steps} />
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex min-w-[88px] items-center justify-end rounded-xl bg-emerald-50 px-3 py-2 text-[11px] font-black text-emerald-700">
                        {fmt(metrics.finalCompleted)}
                      </span>
                    </td>

                    <td className={`px-4 py-3 text-right text-[11px] font-black ${row.stock < 0 ? 'text-rose-600' : 'text-slate-700'}`}>
                      {fmt(row.stock)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center text-slate-300">
                    <div className="flex flex-col items-center gap-2">
                      <Package size={30} />
                      <p className="text-[10px] font-black uppercase tracking-widest">
                        Sin pedidos enviados a produccion para este dia
                      </p>
                      <p className="text-[10px] font-bold text-slate-400">
                        Usa el boton desde logistica o agrega una carga manual.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rowsWithoutWorkflow > 0 ? (
        <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[10px] font-bold text-amber-800">
          Hay {rowsWithoutWorkflow} producto(s) sin flujo configurado. Puedes entrar en la fila para definir los pasos.
        </div>
      ) : null}
    </div>
  );
}
